import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { deleteShopOrderItem, postShopOrderItem, patchShopOrderItem } from '../../services/orders';
import { searchProductWithAkaName } from '../../services/search';
import OrderPaymentsPopView from './OrderPaymentsPopView';
import ShowModal from '../../utils/ShowModal';
import { createDashboardTicket } from '../../services/dashboardTickets';
import { getUserId, getUserRole, isB2bSalesperson } from '../../helpers/local_storage_helpers';
import { searchSupportAssignableUsers } from '../../services/supportTickets';
import { BiCopy } from 'react-icons/bi';

const SUPPORT_TICKET_STATUSES = new Set(['DISPATCHED', 'DELIVERED', 'RETURNED', 'CANCELLED']);

const ticketStatusTone = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'pending') return 'bg-amber-100 text-amber-800';
    if (s === 'resolved' || s === 'closed') return 'bg-green-100 text-green-800';
    if (s === 'failed' || s === 'cancelled') return 'bg-red-100 text-red-800';
    return 'bg-slate-100 text-slate-700';
};

function OrderOrdersPopView({ item, onOperation }) {
    const isStaff = getUserRole() === 'staff';
    const isB2bSales = isB2bSalesperson();
    const [deletingId, setDeletingId] = useState(null);
    const [editingItemId, setEditingItemId] = useState(null);
    const [editDispatchNoteValue, setEditDispatchNoteValue] = useState('');
    const [updatingId, setUpdatingId] = useState(null);

    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [newQty, setNewQty] = useState(1);
    const [newPrice, setNewPrice] = useState(0);
    const [newIsTaxedPrice, setNewIsTaxedPrice] = useState(false);
    const [newDispatchNote, setNewDispatchNote] = useState('');
    const [adding, setAdding] = useState(false);

    // product search state — same pattern as AsyncFormikDropdown
    const [searchInput, setSearchInput] = useState('');
    const [productOptions, setProductOptions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [supportModalOpen, setSupportModalOpen] = useState(false);
    const [supportSubject, setSupportSubject] = useState('');
    const [supportDescription, setSupportDescription] = useState('');
    const [supportPriority, setSupportPriority] = useState('low');
    const [supportImage, setSupportImage] = useState(null);
    const [supportSubmitting, setSupportSubmitting] = useState(false);
    const [supportAssignedTo, setSupportAssignedTo] = useState(null);
    const [assigneeSearchInput, setAssigneeSearchInput] = useState('');
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [assigneeSearching, setAssigneeSearching] = useState(false);

    const orderStatusUpper = String(item?.status || '').toUpperCase();
    const orderInSupportStatus = Boolean(
        item?.id && item?.status && SUPPORT_TICKET_STATUSES.has(orderStatusUpper)
    );

    // Who may raise a support ticket:
    //  - staff: only once the order is DELIVERED
    //  - b2b salesperson (and every other non-staff role, e.g. admin): any support-eligible status
    const canRaiseSupportTicket =
        orderInSupportStatus &&
        (isB2bSales || !isStaff || orderStatusUpper === 'DELIVERED');

    const hasTicketsList = Array.isArray(item?.tickets) && item.tickets.length > 0;
    const showTicketsSection = Boolean(item?.id && (canRaiseSupportTicket || hasTicketsList));

    const resetSupportForm = useCallback(() => {
        setSupportSubject('');
        setSupportDescription('');
        setSupportPriority('low');
        setSupportImage(null);
        setSupportAssignedTo(null);
        setAssigneeSearchInput('');
        setAssigneeOptions([]);
    }, []);

    const closeSupportModal = useCallback(() => {
        setSupportModalOpen(false);
        resetSupportForm();
    }, [resetSupportForm]);

    const handleSupportSubmit = async (e) => {
        e.preventDefault();
        const subject = String(supportSubject || '').trim();
        if (!subject) {
            toast.error('Please enter a problem summary.');
            return;
        }
        if (!item?.id) {
            toast.error('Order is not loaded.');
            return;
        }
        let userId;
        let staffId;
        try {
            userId = item.user_id ?? getUserId();
            staffId = getUserId();
        } catch {
            toast.error('Could not resolve user for this ticket.');
            return;
        }
        const firstLineId = Array.isArray(item.order_items) && item.order_items[0]?.id != null
            ? item.order_items[0].id
            : undefined;

        const payload = {
            category: 'order_issue',
            category_other: '',
            priority: supportPriority === 'medium' || supportPriority === 'high' ? supportPriority : 'low',
            subject,
            description: String(supportDescription || '').trim(),
            assigned_by: staffId,
            assigned_to:
                supportAssignedTo?.value !== undefined &&
                supportAssignedTo?.value !== null &&
                String(supportAssignedTo.value).trim() !== ''
                    ? supportAssignedTo.value
                    : '',
            order: item.id,
            order_id: item.id,
            resolution_date_time_given: '',
            user: userId,
        };
        if (firstLineId !== undefined) {
            payload.order_item = firstLineId;
        }
        if (supportImage instanceof File) {
            payload.image = supportImage;
        }

        setSupportSubmitting(true);
        try {
            const res = await createDashboardTicket(payload);
            if (res?.status === 201) {
                toast.success('Support ticket raised successfully.');
                closeSupportModal();
                onOperation?.();
            } else {
                toast.error('Could not create ticket. Please try again.');
            }
        } catch (err) {
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                (typeof err?.response?.data === 'string' ? err.response.data : null) ||
                'Could not create ticket. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not create ticket. Please try again.');
        } finally {
            setSupportSubmitting(false);
        }
    };

    // debounced search — exact same logic as AsyncFormikDropdown
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchInput) {
                fetchProducts(searchInput);
            }
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [searchInput]);

    useEffect(() => {
        if (!supportModalOpen) return undefined;
        const delayDebounce = setTimeout(async () => {
            try {
                setAssigneeSearching(true);
                const response = await searchSupportAssignableUsers(assigneeSearchInput || '');
                const list = response?.data || [];
                setAssigneeOptions(
                    (Array.isArray(list) ? list : []).map((u) => ({
                        value: u.id,
                        label: `${u.name} | ${u.mobile_number ?? u.phone_number ?? ''} | ${u.role ?? ''}`,
                    }))
                );
            } catch (e) {
                console.error('Assignee search error:', e);
                setAssigneeOptions([]);
            } finally {
                setAssigneeSearching(false);
            }
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [assigneeSearchInput, supportModalOpen]);

    const fetchProducts = async (input) => {
        try {
            setIsSearching(true);
            const response = await searchProductWithAkaName(input);
            const results = response.data.results || response.data;
            const mapped = (Array.isArray(results) ? results : []).map((pro) => ({
                value: pro.id,
                label: pro.aka_name ?? pro.product_name,
                price: pro.price,
            }));
            setProductOptions(mapped);
        } catch (error) {
            console.error('Product search error:', error);
            setProductOptions([]);
        } finally {
            setIsSearching(false);
        }
    };

    const newTotal = (Number(newQty) || 0) * (Number(newPrice) || 0);

    const handleDelete = async (orderItem) => {
        const itemId = orderItem?.id;
        if (!itemId) { toast.error('Item ID not found'); return; }
        setDeletingId(itemId);
        try {
            const res = await deleteShopOrderItem({ id: itemId });
            if (res?.status === 200 || res?.status === 204) {
                toast.success('Item deleted');
                onOperation?.();
            } else {
                toast.error('Unable to delete item');
            }
        } catch (err) {
            console.error('Delete error:', err);
            toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to delete item');
        } finally {
            setDeletingId(null);
        }
    };

    const handleStartEditNote = (orderItem) => {
        setEditingItemId(orderItem.id);
        setEditDispatchNoteValue(orderItem.dispatch_note || '');
    };

    const handleCancelEditNote = () => {
        setEditingItemId(null);
        setEditDispatchNoteValue('');
    };

    const handleSaveNote = async (orderItemId) => {
        setUpdatingId(orderItemId);
        try {
            const res = await patchShopOrderItem({
                id: orderItemId,
                data: {
                    dispatch_note: editDispatchNoteValue.trim() || null
                }
            });
            if (res?.status === 200 || res?.status === 204) {
                toast.success('Dispatch note updated');
                setEditingItemId(null);
                setEditDispatchNoteValue('');
                onOperation?.();
            } else {
                toast.error('Failed to update dispatch note');
            }
        } catch (err) {
            console.error('Update dispatch note error:', err);
            toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to update dispatch note');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleProductSelect = (option) => {
        if (!option) {
            setSelectedProduct(null);
            setNewPrice(0);
            return;
        }
        setSelectedProduct(option);
        setNewPrice(Number(option.price) || 0);
        setNewQty(1);
    };

    const handleAdd = async () => {
        if (!selectedProduct) { toast.error('Select a product first'); return; }
        const orderId = item?.id || item?.order_id;
        if (!orderId) { toast.error('Order ID not found'); return; }
        if ((Number(newQty) || 0) <= 0) { toast.error('Quantity must be > 0'); return; }

        setAdding(true);
        try {
            const payload = {
                order: orderId,
                shop_product: selectedProduct.value,
                quantity: Number(newQty),
                price: String(Number(newPrice) || 0),
                total_price: String(newTotal),
            };
            if (item?.order_type === 'B2B') {
                payload.is_taxed_price = newIsTaxedPrice === true;
            }
            if (newDispatchNote.trim()) {
                payload.dispatch_note = newDispatchNote.trim();
            }
            const res = await postShopOrderItem({
                data: payload,
            });
            if (res?.status === 200 || res?.status === 201) {
                toast.success('Item added');
                resetAddForm();
                onOperation?.();
            } else {
                toast.error('Unable to add item');
            }
        } catch (err) {
            console.error('Add error:', err);
            toast.error(
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                JSON.stringify(err?.response?.data) ||
                'Failed to add item'
            );
        } finally {
            setAdding(false);
        }
    };

    const resetAddForm = () => {
        setSelectedProduct(null);
        setNewQty(1);
        setNewPrice(0);
        setNewIsTaxedPrice(false);
        setNewDispatchNote('');
        setShowAddForm(false);
    };

    return (
        <div className="w-full flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <StyledHeadingText text="Order Items" TextColor={"text-gray-500 font-normal"} />
                {!showAddForm && !isStaff && (
                    <button
                        type="button"
                        onClick={() => setShowAddForm(true)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:opacity-90"
                    >
                        + Add Item
                    </button>
                )}
            </div>

            <ShowModal
                isOpen={supportModalOpen}
                onRequestClose={() => !supportSubmitting && closeSupportModal()}
                contentLabel="Raise Support Ticket"
                title="Raise Support Ticket"
                modalWidth="min(32rem, 92vw)"
                componentToPass={(
                    <form onSubmit={handleSupportSubmit} className="flex flex-col gap-4 pb-2">
                        <div className="flex flex-col gap-1">
                            <label htmlFor="order-support-problem" className="text-xs font-semibold text-gray-600">
                                Problem
                            </label>
                            <input
                                id="order-support-problem"
                                type="text"
                                value={supportSubject}
                                onChange={(e) => setSupportSubject(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                placeholder="Brief summary"
                                disabled={supportSubmitting}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="order-support-description" className="text-xs font-semibold text-gray-600">
                                Description
                            </label>
                            <textarea
                                id="order-support-description"
                                value={supportDescription}
                                onChange={(e) => setSupportDescription(e.target.value)}
                                rows={4}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                                placeholder="Describe the issue"
                                disabled={supportSubmitting}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="order-support-priority" className="text-xs font-semibold text-gray-600">
                                Priority
                            </label>
                            <select
                                id="order-support-priority"
                                value={supportPriority}
                                onChange={(e) => setSupportPriority(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                                disabled={supportSubmitting}
                            >
                                <option value="low">low</option>
                                <option value="medium">medium</option>
                                <option value="high">high</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="order-support-assignee" className="text-xs font-semibold text-gray-600">
                                Assigned To
                            </label>
                            <Select
                                inputId="order-support-assignee"
                                options={assigneeOptions}
                                value={supportAssignedTo}
                                onChange={(opt) => setSupportAssignedTo(opt)}
                                onInputChange={(value) => setAssigneeSearchInput(value)}
                                isSearchable
                                isClearable
                                isLoading={assigneeSearching}
                                placeholder="Search staff / admin / manufacturer…"
                                noOptionsMessage={() =>
                                    assigneeSearchInput ? 'No users found' : 'Type to search…'
                                }
                                isDisabled={supportSubmitting}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: 40,
                                        fontSize: 14,
                                        borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                                        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                                        borderRadius: '8px',
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        fontSize: 13,
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                        border: '1px solid #e5e7eb',
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
                                }}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="order-support-image" className="text-xs font-semibold text-gray-600">
                                Attach Image
                            </label>
                            <input
                                id="order-support-image"
                                type="file"
                                accept="image/*"
                                className="text-sm text-gray-600 file:mr-2 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold"
                                disabled={supportSubmitting}
                                onChange={(e) => setSupportImage(e.target.files?.[0] ?? null)}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => !supportSubmitting && closeSupportModal()}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                                disabled={supportSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={supportSubmitting}
                                className="inline-flex items-center justify-center gap-2 min-w-[8rem] px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {supportSubmitting ? (
                                    <>
                                        <span
                                            className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                                            aria-hidden
                                        />
                                        <span>Submitting…</span>
                                    </>
                                ) : (
                                    'Submit'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            />

            {/* ── add item form ── */}
            {showAddForm && !isStaff && (
                <div className="mb-3 border border-gray-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200 px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        <div className="col-span-4">Product</div>
                        <div className="col-span-2 text-center">Quantity</div>
                        <div className="col-span-2 text-center">Price</div>
                        <div className="col-span-2 text-center">Total</div>
                        <div className="col-span-2 text-center">Action</div>
                    </div>

                    <div className="bg-white p-3 space-y-3">
                        <div className="grid grid-cols-12 items-center gap-2 bg-white">
                            {/* product search — same pattern as AsyncFormikDropdown */}
                            <div className="col-span-4">
                                <Select
                                    options={productOptions}
                                    value={selectedProduct}
                                    onChange={handleProductSelect}
                                    onInputChange={(value) => setSearchInput(value)}
                                    isSearchable
                                    isClearable
                                    isLoading={isSearching}
                                    placeholder="Search product..."
                                    noOptionsMessage={() => searchInput ? 'No products found' : 'Type to search…'}
                                    maxMenuHeight={200}
                                    menuPlacement="auto"
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: 36,
                                            fontSize: 13,
                                            borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                                            boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                                            borderRadius: '8px',
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: 13,
                                            borderRadius: '8px',
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid #e5e7eb',
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                />
                            </div>

                            <div className="col-span-2 flex justify-center">
                                <input
                                    type="number"
                                    min={1}
                                    value={newQty}
                                    onChange={(e) => setNewQty(e.target.value)}
                                    className="w-16 text-center border border-gray-300 rounded-lg px-1.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                                />
                            </div>

                            <div className="col-span-2 flex justify-center">
                                <input
                                    type="number"
                                    min={0}
                                    step="any"
                                    value={newPrice}
                                    onChange={(e) => setNewPrice(e.target.value)}
                                    className="w-20 text-center border border-gray-300 rounded-lg px-1.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                                />
                            </div>

                            <div className="col-span-2 text-center">
                                <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                                    ₹{newTotal.toFixed(2)}
                                </span>
                            </div>

                            <div className="col-span-2 flex justify-center gap-1">
                                <button
                                    type="button"
                                    onClick={handleAdd}
                                    disabled={adding || !selectedProduct}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
                                >
                                    {adding ? 'Adding…' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={resetAddForm}
                                    className="px-2 py-1.5 text-xs text-gray-500 hover:text-red-500"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {item?.order_type === "B2B" ? (
                            <div className="grid grid-cols-12 gap-2 border-t border-dashed border-gray-100 pt-3">
                                <div className="col-span-4 flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="add-item-is-taxed-price"
                                        checked={newIsTaxedPrice}
                                        onChange={(e) => setNewIsTaxedPrice(e.target.checked)}
                                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="add-item-is-taxed-price" className="text-xs font-medium text-gray-700 select-none">
                                        Price includes Tax (Taxed Price)
                                    </label>
                                </div>
                                <div className="col-span-8">
                                    <input
                                        type="text"
                                        value={newDispatchNote}
                                        onChange={(e) => setNewDispatchNote(e.target.value)}
                                        placeholder="Dispatch Note (e.g. Dispatch from Indore warehouse)"
                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-12 gap-2 border-t border-dashed border-gray-100 pt-3">
                                <div className="col-span-12">
                                    <input
                                        type="text"
                                        value={newDispatchNote}
                                        onChange={(e) => setNewDispatchNote(e.target.value)}
                                        placeholder="Dispatch Note (e.g. Dispatch from Indore warehouse)"
                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── existing items ── */}
            {item?.order_items && item.order_items.length !== 0 ? (
                <div className="flex flex-col overflow-y-auto gap-2">
                    {item?.order_items?.map((orderItem, index) => (
                        <div key={orderItem?.id || index} className='p-4 rounded-lg border'>
                            <div className='flex flex-col gap-2'>
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className='font-medium text-gray-800'>{orderItem.aka_name || orderItem.product_name}</h3>
                                    {!isStaff && (
                                        <button
                                            type="button"
                                            disabled={deletingId === orderItem?.id}
                                            onClick={() => handleDelete(orderItem)}
                                            className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {deletingId === orderItem?.id ? 'Deleting…' : 'Delete'}
                                        </button>
                                    )}
                                </div>
                                <div className='grid grid-cols-3 gap-4 mt-2 text-sm'>
                                    <div className='flex flex-col'>
                                        <span className='text-gray-500'>Quantity</span>
                                        <span className='font-medium'>{orderItem.quantity}</span>
                                    </div>
                                    <div className='flex flex-col'>
                                        <span className='text-gray-500'>Unit Price</span>
                                        <span className='font-medium'>
                                            ₹{orderItem.price.toFixed(2)}
                                            {item?.order_type === 'B2B' && (
                                                <span className="text-[10px] text-gray-400 block">
                                                    {orderItem.is_taxed_price ? '(incl. Tax)' : '(excl. Tax)'}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className='flex flex-col'>
                                        <span className='text-gray-500'>Total Price</span>
                                        <span className='font-medium text-green-600'>₹{orderItem.total_price.toFixed(2)}</span>
                                    </div>
                                </div>
                                {!isStaff && (editingItemId === orderItem.id ? (
                                    <div className="mt-2 pt-2 border-t border-dashed border-gray-100 text-xs flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={editDispatchNoteValue}
                                            onChange={(e) => setEditDispatchNoteValue(e.target.value)}
                                            placeholder="Enter dispatch note..."
                                            className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            disabled={updatingId === orderItem.id}
                                        />
                                        <button
                                            type="button"
                                            disabled={updatingId === orderItem.id}
                                            onClick={() => handleSaveNote(orderItem.id)}
                                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg text-xs disabled:opacity-50"
                                        >
                                            {updatingId === orderItem.id ? 'Saving…' : 'Save'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={updatingId === orderItem.id}
                                            onClick={handleCancelEditNote}
                                            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-xs"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mt-2 pt-2 border-t border-dashed border-gray-100 text-xs flex items-center justify-between gap-2">
                                        {orderItem.dispatch_note ? (
                                            <div>
                                                <span className="text-gray-500 font-medium">Dispatch Note: </span>
                                                <span className="text-gray-700 font-semibold">{orderItem.dispatch_note}</span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 italic">No dispatch note</span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleStartEditNote(orderItem)}
                                            className="text-blue-600 hover:text-blue-800 font-semibold shrink-0"
                                        >
                                            {orderItem.dispatch_note ? 'Edit' : '+ Add Note'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            {item?.id ? (
                <div className="mt-6 pt-4 border-t border-gray-100">
                    <OrderPaymentsPopView
                        item={item}
                        onOperation={() => onOperation?.()}
                    />
                </div>
            ) : null}

            {showTicketsSection ? (
                <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <StyledHeadingText text="Tickets" TextColor="text-gray-500 font-normal" />
                        {canRaiseSupportTicket ? (
                            <button
                                type="button"
                                onClick={() => setSupportModalOpen(true)}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            >
                                Raise Support Ticket
                            </button>
                        ) : null}
                    </div>
                    {hasTicketsList ? (
                    <div className="mt-2 flex flex-col gap-2">
                        {item.tickets.map((t, idx) => {
                            const tid = t?.id != null ? String(t.id) : '';
                            return (
                                <div
                                    key={tid || idx}
                                    className="rounded-lg border border-gray-200 bg-white p-3 flex flex-col gap-2"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <p className="text-sm font-medium text-gray-900 flex-1 min-w-0">
                                            {t?.problem || t?.subject || '—'}
                                        </p>
                                        {t?.status != null && t.status !== '' ? (
                                            <span
                                                className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ticketStatusTone(t.status)}`}
                                            >
                                                {String(t.status)}
                                            </span>
                                        ) : null}
                                    </div>
                                    {tid ? (
                                        <div className="flex items-center gap-1.5 min-w-0 border-t border-gray-100 pt-2">
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 shrink-0">
                                                Ticket ID
                                            </span>
                                            <p
                                                className="text-xs font-mono text-gray-600 truncate flex-1 min-w-0"
                                                title={tid}
                                            >
                                                {tid}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(tid).then(
                                                        () => toast.success('Ticket ID copied'),
                                                        () => toast.error('Failed to copy')
                                                    );
                                                }}
                                                className="shrink-0 inline-flex p-1 rounded-md bg-slate-100 hover:bg-slate-200 text-blue-600"
                                                title="Copy ticket ID"
                                                aria-label="Copy ticket ID"
                                            >
                                                <BiCopy size={16} />
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    ) : (
                        <p className="text-xs text-gray-400 mt-1">No tickets yet.</p>
                    )}
                </div>
            ) : null}
        </div>
    );
}

export default OrderOrdersPopView;
